import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const riskFactors = [
  { id: 'developer', label: 'Developer Track Record', weight: 25, options: [
    { label: 'Top-tier (Emaar, Sobha, Meraas)', score: 90 },
    { label: 'Established (DAMAC, Nakheel)', score: 70 },
    { label: 'Mid-tier developer', score: 50 },
    { label: 'New/unknown developer', score: 20 }
  ]},
  { id: 'construction', label: 'Construction Progress', weight: 20, options: [
    { label: 'Above 70% complete', score: 90 },
    { label: '40-70% complete', score: 70 },
    { label: '10-40% complete', score: 45 },
    { label: 'Pre-construction', score: 25 }
  ]},
  { id: 'escrow', label: 'Escrow Compliance', weight: 20, options: [
    { label: 'RERA-approved escrow, audited', score: 95 },
    { label: 'Escrow in place, not audited', score: 70 },
    { label: 'Escrow pending setup', score: 30 },
    { label: 'No escrow information', score: 5 }
  ]},
  { id: 'market', label: 'Market Conditions', weight: 15, options: [
    { label: 'Strong demand, low supply', score: 85 },
    { label: 'Balanced market', score: 65 },
    { label: 'Oversupply in area', score: 40 },
    { label: 'Declining demand', score: 20 }
  ]},
  { id: 'location', label: 'Location Quality', weight: 20, options: [
    { label: 'Prime (Downtown, Marina, Palm)', score: 90 },
    { label: 'Established (JVC, JLT, Business Bay)', score: 75 },
    { label: 'Emerging area', score: 55 },
    { label: 'Remote/undeveloped area', score: 30 }
  ]}
];

const RiskAssessmentTool = () => {
  const [selections, setSelections] = useState({});

  const totalScore = useMemo(() => {
    let weighted = 0;
    let totalWeight = 0;
    riskFactors?.forEach(f => {
      if (selections?.[f?.id] !== undefined) {
        weighted += (selections?.[f?.id] * f?.weight);
        totalWeight += f?.weight;
      }
    });
    return totalWeight > 0 ? Math.round(weighted / totalWeight) : null;
  }, [selections]);

  const getRiskLevel = (score) => {
    if (score >= 75) return { label: 'LOW RISK', color: '#22c55e', bg: 'rgba(34,197,94,0.1)' };
    if (score >= 50) return { label: 'MEDIUM RISK', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)' };
    return { label: 'HIGH RISK', color: '#ef4444', bg: 'rgba(239,68,68,0.1)' };
  };

  const risk = totalScore !== null ? getRiskLevel(totalScore) : null;
  const answered = Object.keys(selections)?.length;

  return (
    <div className="rounded-2xl p-6" style={{ background: 'rgba(10,22,40,0.9)', border: '1px solid rgba(0,212,255,0.2)' }}>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)' }}>
          <Icon name="AlertTriangle" size={20} color="#00D4FF" />
        </div>
        <div>
          <h3 className="text-lg font-bold" style={{ color: '#E8F4FF' }}>Off-Plan Risk Assessment</h3>
          <p className="text-xs" style={{ color: '#6B9EC4' }}>Evaluate investment risks across key factors</p>
        </div>
      </div>
      <div className="space-y-4 mb-6">
        {riskFactors?.map(factor => (
          <div key={factor?.id}>
            <div className="flex justify-between text-xs mb-2">
              <span style={{ color: '#E8F4FF' }}>{factor?.label}</span>
              <span style={{ color: '#6B9EC4' }}>Weight: {factor?.weight}%</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {factor?.options?.map((opt, i) => (
                <button key={i} onClick={() => setSelections(prev => ({ ...prev, [factor?.id]: opt?.score }))}
                  className="text-left p-2 rounded-lg text-xs transition-all"
                  style={{ background: selections?.[factor?.id] === opt?.score ? 'rgba(0,212,255,0.15)' : 'rgba(0,212,255,0.04)', border: `1px solid ${selections?.[factor?.id] === opt?.score ? 'rgba(0,212,255,0.5)' : 'rgba(0,212,255,0.1)'}`, color: selections?.[factor?.id] === opt?.score ? '#E8F4FF' : '#6B9EC4' }}>
                  {opt?.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 rounded-xl text-center" style={{ background: risk ? risk?.bg : 'rgba(0,212,255,0.05)', border: `1px solid ${risk ? risk?.color : 'rgba(0,212,255,0.2)'}` }}>
        {risk ? (
          <>
            <div className="text-3xl font-bold mb-1" style={{ color: risk?.color }}>{totalScore}/100</div>
            <div className="font-bold text-sm" style={{ color: risk?.color }}>{risk?.label}</div>
            <div className="text-xs mt-1" style={{ color: '#6B9EC4' }}>Based on {answered}/{riskFactors?.length} factors assessed</div>
          </>
        ) : (
          <div className="text-sm" style={{ color: '#6B9EC4' }}>Select options above to calculate risk score ({answered}/{riskFactors?.length} answered)</div>
        )}
      </div>
    </div>
  );
};

// Need to import useMemo
import { useMemo } from 'react';

export default RiskAssessmentTool;
